/* eslint-disable no-script-url,jsx-a11y/anchor-is-valid */
import React, { memo, useCallback, useEffect, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { shallowEqual, useSelector } from "react-redux";

const formatFee = (v) => `$${Number(v).toFixed(2)}`;

// Custom fee is a per-program override: blank inherits the branch default
// (default_fee from the courses endpoint). default_fee null = no default set at
// this branch; undefined = not known yet (e.g. still being looked up).
const customFeeLabel = (defaultFee) => {
  if (defaultFee === undefined)
    return "Custom fee (leave blank to use the branch default)";
  if (defaultFee === null) return "Custom fee (no branch default set)";
  return `Custom fee (leave blank to use branch default ${formatFee(
    defaultFee
  )})`;
};

// Custom Fields component that manages its own local state
const CustomFields = memo(
  ({
    courseId,
    initialFee,
    defaultFee,
    initialDescription,
    onUpdateCustomField,
  }) => {
    const [localFee, setLocalFee] = useState(initialFee ?? "");
    const [localDescription, setLocalDescription] = useState(
      initialDescription || ""
    );

    // Sync with parent state if it changes externally
    useEffect(() => {
      setLocalFee(initialFee ?? "");
    }, [initialFee]);

    useEffect(() => {
      setLocalDescription(initialDescription || "");
    }, [initialDescription]);

    const handleFeeBlur = useCallback(() => {
      if (String(localFee) !== String(initialFee ?? "")) {
        onUpdateCustomField(courseId, "custom_fee", localFee);
      }
    }, [courseId, localFee, initialFee, onUpdateCustomField]);

    const handleDescriptionBlur = useCallback(() => {
      if (localDescription !== (initialDescription || "")) {
        onUpdateCustomField(
          courseId,
          "custom_course_description",
          localDescription
        );
      }
    }, [courseId, localDescription, initialDescription, onUpdateCustomField]);

    return (
      <div className="mt-3">
        <div className="form-group mb-2">
          <label className="font-size-sm text-muted">
            {customFeeLabel(defaultFee)}
          </label>
          <input
            type="number"
            step="0.01"
            min="0"
            className="form-control form-control-sm"
            placeholder={
              defaultFee != null
                ? `${formatFee(defaultFee)} (branch default)`
                : "Enter fee"
            }
            value={localFee}
            onChange={(e) => setLocalFee(e.target.value)}
            onBlur={handleFeeBlur}
          />
          {defaultFee === null && localFee === "" && (
            <small className="form-text text-warning">
              No fee set — this program will be call-to-enroll for this
              course.
            </small>
          )}
        </div>
        <div className="form-group mb-2">
          <label className="font-size-sm text-muted">Custom Description</label>
          <textarea
            rows="4"
            className="form-control form-control-sm"
            placeholder="Enter description"
            value={localDescription}
            onChange={(e) => setLocalDescription(e.target.value)}
            onBlur={handleDescriptionBlur}
            maxLength={400}
          />
        </div>
      </div>
    );
  }
);

CustomFields.displayName = "CustomFields";

// Course Item component moved outside and memoized
const CourseItem = memo(
  ({
    course,
    showActions = true,
    showToggle = true,
    isAddButton = false,
    allowCustomFields = false,
    onUpdateCustomField,
    onToggleCourse,
    onRemoveCourse,
    onAddCourse,
    loading,
    isAddDisabled = false,
    addDisabledReason = "",
  }) => {
    const addButton = (
      <button
        className="btn btn-icon btn-light btn-hover-danger btn-sm"
        onClick={() => onAddCourse(course)}
        disabled={loading || isAddDisabled}
        style={{ opacity: isAddDisabled ? 0.5 : 1 }}
      >
        <i className="fa fa-plus text-success"></i>
      </button>
    );

    return (
      <div className="mb-6">
        <div className="d-flex align-items-start flex-grow-1">
          <div className="d-flex flex-wrap align-items-start justify-content-between w-100">
            <div className={`d-flex flex-column py-2 w-75`}>
              <a
                href="#"
                className="text-dark-75 font-weight-bold text-hover-primary font-size-lg mb-1"
              >
                {course.course_name}
              </a>
              <span className="text-muted font-weight-bold mb-2">
                {course.course_code}
              </span>
              {/* Red only where it actually blocks something. On a course
                  waiting to be added it explains the disabled + button. On one
                  already in the batch it is information: the save is not
                  blocked, and 120 active batches are in this state — showing it
                  as an error invites admins to remove a stream to "fix" it,
                  which is destructive and pointless. */}
              {course.fee_exists === 0 && (
                <span
                  className={`font-size-sm mb-2 ${
                    isAddButton
                      ? "text-danger font-weight-bold"
                      : "text-muted font-italic"
                  }`}
                >
                  <i
                    className={`fa fa-exclamation-triangle mr-1 ${
                      isAddButton ? "text-danger" : "text-muted"
                    }`}
                  ></i>
                  {isAddButton
                    ? "No pricing at this batch's duration"
                    : "No pricing at this batch's duration — existing enrolments are unaffected"}
                </span>
              )}
              {showActions && allowCustomFields && (
                <CustomFields
                  courseId={course.course_id}
                  initialFee={course.custom_fee}
                  defaultFee={course.default_fee}
                  initialDescription={course.custom_course_description}
                  onUpdateCustomField={onUpdateCustomField}
                />
              )}
            </div>

            <div
              className="d-flex align-items-start"
              style={{ marginTop: "8px" }}
            >
              {showActions && (
                <>
                  {showToggle && (course.show_ind == 1 ? (
                    <OverlayTrigger overlay={<Tooltip>Hide Stream</Tooltip>}>
                      <button
                        className="btn btn-icon btn-light btn-hover-muted btn-sm mr-2"
                        onClick={() => onToggleCourse(course)}
                        disabled={loading}
                      >
                        <i className="fa fa-eye-slash text-danger"></i>
                      </button>
                    </OverlayTrigger>
                  ) : (
                    <OverlayTrigger overlay={<Tooltip>Show Stream</Tooltip>}>
                      <button
                        className="btn btn-icon btn-light btn-hover-primary btn-sm mr-2"
                        onClick={() => onToggleCourse(course)}
                        disabled={loading}
                      >
                        <i className="fa fa-eye text-success"></i>
                      </button>
                    </OverlayTrigger>
                  ))}

                  <OverlayTrigger overlay={<Tooltip>Remove Stream</Tooltip>}>
                    <button
                      className="btn btn-icon btn-light btn-hover-danger btn-sm"
                      onClick={() => onRemoveCourse(course)}
                      disabled={loading}
                    >
                      <i className="fa fa-minus text-danger"></i>
                    </button>
                  </OverlayTrigger>
                </>
              )}

              {isAddButton && (
                <>
                  {isAddDisabled ? (
                    <OverlayTrigger
                      overlay={<Tooltip>{addDisabledReason}</Tooltip>}
                    >
                      <span className="d-inline-block">{addButton}</span>
                    </OverlayTrigger>
                  ) : (
                    <OverlayTrigger overlay={<Tooltip>Add Stream</Tooltip>}>
                      {addButton}
                    </OverlayTrigger>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
);

CourseItem.displayName = "CourseItem";

export function CourseListSelection({
  className,
  id,
  type,
  setCoursesToAdd,
  coursesToAdd,
  setCoursesToRemove,
  coursesToRemove,
  batchCourses,
  campCourses,
  setBatchCourses,
  setCampCourses,
  batchMissingCourses,
  campMissingCourses,
  setBatchMissingCourses,
  setCampMissingCourses,
  allowCustomFields = false,
  onUpdateCustomField,
  showToggle = true,
  maxHeight,
}) {
  //getbranch
  const branch = useSelector(
    (state) => state.auth.branches.currentBranch,
    shallowEqual
  );

  const [refetch, setRefetch] = useState(false);
  const [loading, setLoading] = useState(false);

  const toggleCourse = async (mainCourses, setMainCourses, course) => {
    const isItInAdded = coursesToAdd.filter((c) => c.id === course.course_id);

    if (isItInAdded.length > 0) {
      const r = coursesToAdd.filter((c) => c.id !== course.course_id);
      let newArr = [
        {
          id: course.course_id,
          show_ind: isItInAdded[0].show_ind == 1 ? 0 : 1,
          custom_fee: isItInAdded[0].custom_fee ?? "",
          custom_course_description:
            isItInAdded[0].custom_course_description || "",
        },
        ...r,
      ];
      setCoursesToAdd(newArr);
    }

    let newBC = mainCourses.filter((bc) => {
      return bc.course_id !== course.course_id;
    });
    let editedCourse = mainCourses.filter((bc) => {
      return bc.course_id == course.course_id;
    })[0];
    editedCourse.show_ind = editedCourse.show_ind == 1 ? 0 : 1;
    let newBCArr = [editedCourse, ...newBC];
    setMainCourses(newBCArr);
  };

  const removeCourse = async (course) => {
    const isItInAdded = coursesToAdd.filter((c) => c.id === course.course_id);

    if (isItInAdded.length > 0) {
      const r = coursesToAdd.filter((c) => c.id != course.course_id);
      setCoursesToAdd(r);
      if (type == "batch") {
        const removeFromCourses = batchCourses.filter(
          (c) => c.course_id != course.course_id
        );
        setBatchCourses(removeFromCourses);
        setBatchMissingCourses([course, ...batchMissingCourses]);
      } else if (type == "camp") {
        const removeFromCourses = campCourses.filter(
          (c) => c.course_id != course.course_id
        );
        setCampCourses(removeFromCourses);
        setCampMissingCourses([course, ...campMissingCourses]);
      }
    } else {
      if (type == "batch") {
        course.course_id &&
          setCoursesToRemove([course.course_id, ...coursesToRemove]);
        const removeFromCourses = batchCourses.filter(
          (c) => c.course_id != course.course_id
        );
        setBatchCourses(removeFromCourses);
        setBatchMissingCourses([course, ...batchMissingCourses]);
      } else if (type == "camp") {
        course.course_id &&
          setCoursesToRemove([course.course_id, ...coursesToRemove]);
        const removeFromCourses = campCourses.filter(
          (c) => c.course_id != course.course_id
        );
        setCampCourses(removeFromCourses);
        setCampMissingCourses([course, ...campMissingCourses]);
      }
    }
  };

  const addCourse = async (course) => {
    const isItInRemove = coursesToRemove.filter((c) => c === course.course_id);

    if (isItInRemove.length > 0) {
      const r = coursesToRemove.filter((c) => c != course.course_id);
      setCoursesToRemove(r);

      if (type == "batch") {
        let newCourse = {
          ...course,
          show_ind: 1,
          custom_fee: "",
          custom_course_description: "",
        };
        setBatchCourses([newCourse, ...batchCourses]);
        const removeFromCourses = batchMissingCourses.filter(
          (c) => c.course_id != course.course_id
        );
        setBatchMissingCourses(removeFromCourses);
      } else if (type == "camp") {
        let newCourse = {
          ...course,
          show_ind: 1,
          custom_fee: "",
          custom_course_description: "",
        };
        setCampCourses([newCourse, ...campCourses]);
        const removeFromCourses = campMissingCourses.filter(
          (c) => c.course_id != course.course_id
        );
        setCampMissingCourses(removeFromCourses);
      }
    } else {
      if (type == "batch") {
        batchCourses.filter((c) => c.course_id === course.course_id).length <
          1 &&
          setCoursesToAdd([
            {
              id: course.course_id,
              show_ind: 1,
              custom_fee: "",
              custom_course_description: "",
            },
            ...coursesToAdd,
          ]);
        setBatchCourses([
          {
            ...course,
            custom_fee: "",
            custom_course_description: "",
          },
          ...batchCourses,
        ]);
        const removeFromCourses = batchMissingCourses.filter(
          (c) => c.course_id != course.course_id
        );
        setBatchMissingCourses(removeFromCourses);
      } else if (type == "camp") {
        campCourses.filter((c) => c.course_id === course.course_id).length <
          1 &&
          setCoursesToAdd([
            {
              id: course.course_id,
              show_ind: 1,
              custom_fee: "",
              custom_course_description: "",
            },
            ...coursesToAdd,
          ]);
        setCampCourses([
          {
            ...course,
            custom_fee: "",
            custom_course_description: "",
          },
          ...campCourses,
        ]);
        const removeFromCourses = campMissingCourses.filter(
          (c) => c.course_id != course.course_id
        );
        setCampMissingCourses(removeFromCourses);
      }
    }
  };

  // Memoize callbacks to prevent unnecessary re-renders
  const handleToggleCourse = useCallback(
    (course) => {
      if (type === "batch") {
        toggleCourse(batchCourses, setBatchCourses, course);
      } else {
        toggleCourse(campCourses, setCampCourses, course);
      }
    },
    [type, batchCourses, campCourses, coursesToAdd]
  );

  const handleRemoveCourse = useCallback(
    (course) => {
      removeCourse(course);
    },
    [
      coursesToAdd,
      coursesToRemove,
      batchCourses,
      campCourses,
      batchMissingCourses,
      campMissingCourses,
      type,
    ]
  );

  const handleAddCourse = useCallback(
    (course) => {
      addCourse(course);
    },
    [
      coursesToAdd,
      coursesToRemove,
      batchCourses,
      campCourses,
      batchMissingCourses,
      campMissingCourses,
      type,
    ]
  );

  // School programs (the only caller passing allowCustomFields) hold exactly
  // one course: a school running two courses in the same slot is set up as two
  // programs, each with its own capacity and "Register on Website" setting.
  // Bulk upload relies on this too -- it enrols everyone into the program's
  // single course.
  //
  // The displayed list already reflects pending changes: addCourse pushes a
  // course into batchCourses/campCourses as well as coursesToAdd, and
  // removeCourse filters it out as well as pushing it onto coursesToRemove.
  // So its length IS the post-save count; adding coursesToAdd on top would
  // count every pending add twice.
  const currentCourses = type === "batch" ? batchCourses : campCourses;
  const netCourseCount = currentCourses.length;
  const cannotAdd = allowCustomFields && netCourseCount >= 1;

  // fee_exists comes from the batch course endpoints: 0 means there is no
  // branch_course_fee priced at this batch's duration, so adding the course
  // would enrol students with no fee attached at all. The server rejects the
  // save either way -- this just stops the admin getting that far.
  const hasNoFee = (course) => course.fee_exists === 0;

  // Determine the appropriate tooltip message
  const getAddDisabledReason = (course) => {
    if (cannotAdd) {
      return "One course per program. To offer another course at this school, create a separate program for it.";
    }
    if (hasNoFee(course)) {
      return "No pricing exists for this course at this batch's duration. Add a fee for that duration under Branch Courses, or change the batch time.";
    }
    return "";
  };

  return (
    <div className={`card card-custom ${className}`}>
      {/* Header */}
      <div className="card-header border-0"></div>

      {/* Body */}
      <div className="card-body pt-0 row">
        <div className="col-md-6 p-20 rounded bg-light-primary" style={maxHeight ? { maxHeight, overflowY: "auto" } : undefined}>
          <div className="row d-flex align-items-center justify-content-between mb-3">
            <h5 className="mb-0">Added Courses</h5>
            {allowCustomFields && (
              <span className="badge badge-primary ml-2">
                1 course per program
              </span>
            )}
          </div>

          {type == "batch" &&
            batchCourses &&
            batchCourses
              .sort((a, b) => {
                const codeA = a.course_code.match(/\d+/g);
                const codeB = b.course_code.match(/\d+/g);
                return codeA - codeB;
              })
              .map((course) => (
                <CourseItem
                  key={course.course_id}
                  course={course}
                  showActions={true}
                  allowCustomFields={allowCustomFields}
                  onUpdateCustomField={onUpdateCustomField}
                  showToggle={showToggle}
                  onToggleCourse={handleToggleCourse}
                  onRemoveCourse={handleRemoveCourse}
                  onAddCourse={handleAddCourse}
                  loading={loading}
                />
              ))}

          {type == "camp" &&
            campCourses &&
            campCourses
              .sort((a, b) => {
                const codeA = a.course_code.match(/\d+/g);
                const codeB = b.course_code.match(/\d+/g);
                return codeA - codeB;
              })
              .map((course) => (
                <CourseItem
                  key={course.course_id}
                  course={course}
                  showActions={true}
                  allowCustomFields={allowCustomFields}
                  onUpdateCustomField={onUpdateCustomField}
                  showToggle={showToggle}
                  onToggleCourse={handleToggleCourse}
                  onRemoveCourse={handleRemoveCourse}
                  onAddCourse={handleAddCourse}
                  loading={loading}
                />
              ))}
        </div>

        <div className="col-md-6 p-20" style={maxHeight ? { maxHeight, overflowY: "auto" } : undefined}>
          <div className="row">
            <h5> Courses To Be Added</h5>
          </div>

          {type == "batch" &&
            batchMissingCourses &&
            batchMissingCourses
              .sort((a, b) => {
                const codeA = a.course_code.match(/\d+/g);
                const codeB = b.course_code.match(/\d+/g);
                return codeA - codeB;
              })
              .map((course) => (
                <CourseItem
                  key={course.course_id}
                  course={course}
                  showActions={false}
                  isAddButton={true}
                  allowCustomFields={allowCustomFields}
                  onUpdateCustomField={onUpdateCustomField}
                  showToggle={showToggle}
                  onToggleCourse={handleToggleCourse}
                  onRemoveCourse={handleRemoveCourse}
                  onAddCourse={handleAddCourse}
                  loading={loading}
                  isAddDisabled={cannotAdd || hasNoFee(course)}
                  addDisabledReason={getAddDisabledReason(course)}
                />
              ))}

          {type == "camp" &&
            campMissingCourses &&
            campMissingCourses
              .sort((a, b) => {
                const codeA = a.course_code.match(/\d+/g);
                const codeB = b.course_code.match(/\d+/g);
                return codeA - codeB;
              })
              .map((course) => (
                <CourseItem
                  key={course.course_id}
                  course={course}
                  showActions={false}
                  isAddButton={true}
                  allowCustomFields={allowCustomFields}
                  onUpdateCustomField={onUpdateCustomField}
                  showToggle={showToggle}
                  onToggleCourse={handleToggleCourse}
                  onRemoveCourse={handleRemoveCourse}
                  onAddCourse={handleAddCourse}
                  loading={loading}
                  isAddDisabled={cannotAdd || hasNoFee(course)}
                  addDisabledReason={getAddDisabledReason(course)}
                />
              ))}
        </div>
      </div>
    </div>
  );
}